import type { Vec2 } from '../../../../geo/types';
import type { Frame, MeshBuilder } from '../../../mesh/meshBuilder';
import { buttress, type ButtressSpec, type MasonryStyles } from '../masonry';
import { outsideDistance, type PlanFrame } from '../planFrame';

/**
 * The traced footprint (the present church, from OSM) draws the walls and only a few corner
 * buttresses, so a buttress stands out only as far as the trace leaves room: its outer corners stay
 * within the tolerance every landmark keeps to its footprint. Where there is no room it is left out.
 */
const ROOM = { tolerance: 0.45, step: 0.05, min: 0.2 } as const;

/** What a church builder hands a buttress: where to build, in what, and the trace to keep to. */
export interface ButtressSite {
  readonly builder: MeshBuilder;
  readonly frame: Frame;
  readonly stone: MasonryStyles;
  readonly plan: { readonly frame: PlanFrame };
}

function depthFor(s: ButtressSite, at: Vec2, outward: Vec2, width: number, max: number): number {
  const along: Vec2 = [-outward[1], outward[0]];
  const fits = (depth: number): boolean =>
    [-1, 1].every((k) => {
      const corner: Vec2 = [at[0] + (along[0] * k * width) / 2 + outward[0] * depth, at[1] + (along[1] * k * width) / 2 + outward[1] * depth];
      return outsideDistance(s.plan.frame, corner) <= ROOM.tolerance;
    });
  for (let depth = max; depth >= ROOM.min; depth -= ROOM.step) if (fits(depth)) return depth;
  return 0;
}

/** A stone-capped buttress at `at` standing out along `outward`, as deep as the trace allows up to `spec.depth`. */
export function fittedButtress(s: ButtressSite, at: Vec2, outward: Vec2, spec: ButtressSpec): void {
  const depth = depthFor(s, at, outward, spec.width, spec.depth);
  if (depth === 0) return;
  buttress(s.builder, { ...s.stone, roof: s.stone.wall }, s.frame, at, outward, { ...spec, depth });
}
